import { DateTime } from 'luxon';
import { always, and, any, cond, equals, gt, gte, isNil, lt, not, or } from 'ramda';
import React, { FC, useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';

import { TintTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface IDateBoxProps {
	label: string;
	className?: string;
	disabled?: boolean;
	error?: boolean;
	errorLabel?: string;
	name?: string;
	value?: string;
	variant?: 'day' | 'month';
	registerFunc?: UseFormReturn['register'];
	setValueFunc?: UseFormReturn['setValue'];
}

const DateField = styled.div<{ error: boolean; disabled: boolean }>`
	background: ${theme.colors.white};
	cursor: text;
	border: ${({ error, disabled }) =>
		error
			? `1px solid ${theme.colors.transparent}`
			: disabled
			? `1px solid ${theme.tints.lighten70(theme.colors.black)}`
			: `1px solid ${theme.colors.black}`};
	display: flex;
	gap: 0.4rem;
	outline: ${({ error }) =>
		error ? `1px solid ${theme.tints.darken40(theme.colors.red)}` : 'none'};
	padding: 2rem 1rem;
	width: auto;
`;

const DateInput = styled.input`
	border: none;
	outline: none;
	font-family: Chalet-LondonSixty, sans-serif;
	font-size: ${({ theme: components }) => components.basicText.body.fontSize.mobile};
	width: ${theme.spacing.lg};
	padding: 0;
	text-align: center;

	@media ${theme.responsive.breakpoints.tablet} {
		font-size: ${({ theme: components }) => components.basicText.body.fontSize.tablet};
	}

	&:disabled {
		background-color: ${theme.colors.transparent};
		color: ${theme.tints.lighten70(theme.colors.black)};
	}

	&::placeholder {
		color: ${theme.tints.lighten70(theme.colors.black)};
	}
`;

const YearInput = styled(DateInput)`
	width: ${theme.spacing.lgxl};
`;

const SepratorText = styled(BasicText)`
	user-select: none;
`;

const ErrorBox = styled.span`
	color: ${({ theme: components }) => components.textInput.errorColor};
	font-size: ${({ theme: components }) => components.textInput.errorFontSize};
	line-height: ${({ theme: components }) => components.textInput.errorLineHeight};
`;

const DateBox: FC<IDateBoxProps> = ({
	className,
	disabled = false,
	error = false,
	errorLabel,
	label,
	name = '',
	registerFunc = () => {},
	setValueFunc = () => {},
	value,
	variant = 'day',
}) => {
	const format = cond([
		[equals('day'), always('MM/dd/yyyy')],
		[equals('month'), always('MM/yyyy')],
	])(variant);

	const getDateValue = (v: string) => {
		const date = DateTime.fromISO(v).isValid ? DateTime.fromISO(v) : DateTime.fromFormat(v, format);
		return {
			day: not(isNaN(date?.day)) ? date?.day?.toString().padStart(2, '0') ?? '' : '',
			month: not(isNaN(date?.month)) ? date?.month?.toString().padStart(2, '0') ?? '' : '',
			year: not(isNaN(date?.year)) ? date?.year?.toString() ?? '' : '',
		};
	};

	const dateValue = value ? getDateValue(value) : { day: '', month: '', year: '' };

	const [dayValue, setDayValue] = useState<string | undefined>(dateValue?.day);
	const [monthValue, setMonthValue] = useState<string | undefined>(dateValue?.month);
	const [yearValue, setYearValue] = useState<string | undefined>(dateValue?.year);

	const getDate = () =>
		cond([
			[equals('day'), always(`${monthValue}/${dayValue}/${yearValue}`)],
			[equals('month'), always(`${monthValue}/${yearValue}`)],
		])(variant);

	type GetSeparatorTint = (
		values: Array<string | undefined>,
		disabled: boolean
	) => TintTypes | undefined;
	const getSeparatorTint: GetSeparatorTint = (values, disabled) => {
		const tint = or(
			any((value) => isNil(value), values),
			disabled
		)
			? 'lighten70'
			: undefined;
		return tint;
	};

	const dayRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);

	const lowerBoundary = 1;
	const dayLowerBoundary = 3;
	const upperBoundary = 10;
	const monthUpperBoundary = 12;

	useEffect(() => {
		if (DateTime.fromFormat(getDate(), format).isValid) {
			setValueFunc(name, DateTime.fromFormat(getDate(), format).toISO(), { shouldValidate: true });
			return;
		}

		if (monthValue && gt(monthValue as unknown as number, monthUpperBoundary)) {
			setMonthValue(monthUpperBoundary.toString());
			return;
		}

		if (equals(2, monthValue?.length) && lt(monthValue as unknown as number, lowerBoundary)) {
			setMonthValue(lowerBoundary.toString().padStart(2, '0'));
			return;
		}

		if (equals(2, dayValue?.length) && lt(dayValue as unknown as number, lowerBoundary)) {
			setDayValue(lowerBoundary.toString().padStart(2, '0'));
			return;
		}

		const daysInMonth = DateTime.fromFormat(`${monthValue}/${yearValue}`, 'MM/yyyy')?.daysInMonth;
		if (
			dayValue &&
			equals(4, yearValue?.length) &&
			not(isNaN(daysInMonth)) &&
			gt(dayValue as unknown as number, daysInMonth)
		) {
			setDayValue(daysInMonth.toString());
			return;
		}

		setValueFunc(name, DateTime.fromFormat(getDate(), format).toISO(), { shouldValidate: true });
	}, [dayValue, monthValue, yearValue]);

	const day = (
		<DateInput
			ref={dayRef}
			aria-label={`${label} Day`}
			disabled={disabled}
			maxLength={2}
			placeholder="DD"
			value={dayValue}
			onChange={(event) => {
				let currentValue: unknown = event.target.value.replace(/[^0-9]/g, '');
				currentValue = and(
					gt(currentValue as number, dayLowerBoundary),
					lt(currentValue as number, upperBoundary)
				)
					? (currentValue as string).padStart(2, '0')
					: currentValue;
				event.target.value = currentValue as string;
				setDayValue(currentValue ? (currentValue as string) : undefined);

				const value = currentValue as number;
				if (
					or(gte(value, upperBoundary), and(gt(value, dayLowerBoundary), lt(value, upperBoundary)))
				) {
					yearRef.current?.focus();
				}
			}}
			onBlur={(event) => {
				setDayValue(event.target.value?.padStart(2, '0'));
			}}
			onFocus={() => {
				dayRef.current?.select();
			}}
			onClick={(e) => {
				dayRef.current?.select();
				e.stopPropagation();
			}}
		/>
	);
	const month = (
		<DateInput
			ref={monthRef}
			aria-label={`${label} Month`}
			disabled={disabled}
			maxLength={2}
			placeholder="MM"
			value={monthValue}
			onChange={(event) => {
				let currentValue: unknown = event.target.value.replace(/[^0-9]/g, '');
				currentValue = and(
					gt(currentValue as number, lowerBoundary),
					lt(currentValue as number, upperBoundary)
				)
					? (currentValue as string).padStart(2, '0')
					: currentValue;
				event.target.value = currentValue as string;
				setMonthValue(currentValue ? (currentValue as string) : undefined);

				const value = currentValue as number;
				if (
					or(gte(value, upperBoundary), and(gt(value, lowerBoundary), lt(value, upperBoundary)))
				) {
					variant === 'day' ? dayRef.current?.focus() : yearRef.current?.focus();
				}
			}}
			onBlur={(event) => {
				setMonthValue(event.target.value?.padStart(2, '0'));
			}}
			onFocus={() => {
				monthRef.current?.select();
			}}
			onClick={(e) => {
				monthRef.current?.select();
				e.stopPropagation();
			}}
		/>
	);
	const year = (
		<YearInput
			ref={yearRef}
			aria-label={`${label} Year`}
			disabled={disabled}
			maxLength={4}
			placeholder="YYYY"
			value={yearValue}
			onChange={(event) => {
				const currentValue: unknown = event.target.value.replace(/[^0-9]/g, '');
				event.target.value = currentValue as string;
				setYearValue(currentValue ? (currentValue as string) : undefined);
			}}
			onFocus={() => {
				yearRef.current?.select();
			}}
			onClick={(e) => {
				yearRef.current?.select();
				e.stopPropagation();
			}}
		/>
	);

	const dateField = (
		<DateField
			error={error}
			disabled={disabled}
			className={className}
			onClick={() => {
				if (!monthValue) {
					monthRef.current?.select();
					return;
				}
				if (!dayValue) {
					dayRef.current?.select();
					return;
				}
				if (!yearValue) {
					yearRef.current?.select();
					return;
				}
				monthRef.current?.select();
			}}>
			{month}
			<SepratorText
				color="black"
				tint={getSeparatorTint([monthValue, variant === 'day' ? dayValue : yearValue], disabled)}>
				/
			</SepratorText>
			{variant === 'day' && (
				<>
					{day}
					<SepratorText color="black" tint={getSeparatorTint([dayValue, yearValue], disabled)}>
						/
					</SepratorText>
				</>
			)}
			{year}
			<input type="hidden" {...registerFunc(name)} onFocus={() => monthRef.current?.focus()} />
		</DateField>
	);

	return (
		<FlexColumn gap="xs">
			{label && <BasicText variant="field">{label}</BasicText>}
			{dateField}
			{error && <ErrorBox>{errorLabel}</ErrorBox>}
		</FlexColumn>
	);
};

export default DateBox;
DateBox.displayName = 'DateBox';
